/**
 * Home visit massager list API service (訪問予定表 施術師毎 — /visits/massager_init).
 */

import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import { parseFilenameFromHeader } from '@/utils/blobDownload';
import type {
  HomeVisitMassagerSearchParams,
  HomeVisitMassagerSearchResponse,
  HomeVisitMassagerMassagersResponse,
  PrintHomeVisitMassagerPayload,
  ExportHomeVisitMassagerCsvPayload,
} from '@/pages/main/homeVisitMassager/types';

function toParams(
  filters: HomeVisitMassagerSearchParams
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value as string | number;
    }
  });
  return params;
}

export async function searchHomeVisitMassagers(
  siteType: string,
  clinicNumber: string,
  filters: HomeVisitMassagerSearchParams,
  serviceType: string = SERVICE_TYPE_A2
): Promise<HomeVisitMassagerSearchResponse> {
  const response = await apiClient.get<HomeVisitMassagerSearchResponse>(
    clinicApiPath(siteType, clinicNumber, '/visits/massager_init/search'),
    {
      params: toParams(filters),
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

export async function getHomeVisitMassagerMassagers(
  siteType: string,
  clinicNumber: string,
  serviceType: string = SERVICE_TYPE_A2
): Promise<HomeVisitMassagerMassagersResponse> {
  const response = await apiClient.get<HomeVisitMassagerMassagersResponse>(
    clinicApiPath(siteType, clinicNumber, '/visits/massager_init/massagers'),
    {
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

export async function printHomeVisitMassagers(
  siteType: string,
  clinicNumber: string,
  payload: PrintHomeVisitMassagerPayload,
  serviceType: string = SERVICE_TYPE_A2
): Promise<Blob> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/visits/massager_list'),
    payload,
    {
      responseType: 'blob',
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data as Blob;
}

export async function exportHomeVisitMassagersCsv(
  siteType: string,
  clinicNumber: string,
  payload: ExportHomeVisitMassagerCsvPayload,
  serviceType: string = SERVICE_TYPE_A2
): Promise<{ blob: Blob; filename: string | null }> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/visits/massager_csv'),
    payload,
    {
      responseType: 'blob',
      headers: { 'X-Service-Type': serviceType },
    }
  );
  const filename = parseFilenameFromHeader(
    (response.headers?.['content-disposition'] ?? null) as string | null
  );
  return { blob: response.data as Blob, filename };
}
